import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userMenus, setUserMenus] = useState([]);
  const [planTier, setPlanTier] = useState(null);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [branchName, setBranchName] = useState(null);
  const [branchAssignments, setBranchAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyCountry, setCompanyCountry] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const storedUser = sessionStorage.getItem('user');
    const storedPlanTier = sessionStorage.getItem('planTier');
    const storedFeatureFlags = sessionStorage.getItem('featureFlags');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        if (storedPlanTier) setPlanTier(storedPlanTier);
        if (storedFeatureFlags) setFeatureFlags(JSON.parse(storedFeatureFlags));
        const storedCountry = sessionStorage.getItem('companyCountry');
        if (storedCountry) setCompanyCountry(storedCountry);
        // Restore branch context
        const storedBranchId = sessionStorage.getItem('branchId');
        const storedBranchName = sessionStorage.getItem('branchName');
        const storedBranchAssignments = sessionStorage.getItem('branchAssignments');
        if (storedBranchId) setBranchId(JSON.parse(storedBranchId));
        if (storedBranchName) setBranchName(storedBranchName);
        if (storedBranchAssignments) setBranchAssignments(JSON.parse(storedBranchAssignments));
        fetchUserMenus(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('planTier');
        sessionStorage.removeItem('featureFlags');
        sessionStorage.removeItem('branchId');
        sessionStorage.removeItem('branchName');
        sessionStorage.removeItem('branchAssignments');
      }
    }

    setLoading(false);
  }, []);

  const fetchUserMenus = async (currentUser) => {
    // Super admin uses hardcoded menus in Layout — no need to fetch
    if (currentUser?.role === 'SUPER_ADMIN') {
      setUserMenus([]);
      return;
    }

    // Always clear cached menus so fresh data is used
    sessionStorage.removeItem('userMenus');
    const defaultMenus = getDefaultMenusByRole(currentUser?.role);

    try {
      const response = await axios.get('/menus/my-menus');
      const menus = response.data.data || response.data;

      if (menus && menus.length > 0) {
        // Database-defined menus are the single source of truth for all roles
        const sorted = [...menus].sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));
        setUserMenus(sorted);
        sessionStorage.setItem('userMenus', JSON.stringify(sorted));
      } else {
        setUserMenus(defaultMenus);
        sessionStorage.setItem('userMenus', JSON.stringify(defaultMenus));
      }
    } catch (error) {
      console.error('Error fetching user menus:', error);
      setUserMenus(defaultMenus);
      sessionStorage.setItem('userMenus', JSON.stringify(defaultMenus));
    }
  };

  const getDefaultMenusByRole = (role) => {
    const allMenus = [
      { key: 'dashboard',          label: 'Dashboard',          path: '/',                        sortOrder: 1 },
      { key: 'enquiries',          label: 'Enquiries',          path: '/enquiries',               sortOrder: 2 },
      { key: 'parties',            label: 'Parties',            path: null,                       sortOrder: 3 },
      { key: 'customers',          label: 'Customers',          path: '/customers',               sortOrder: 1, parentId: 'parties' },
      { key: 'vendors',            label: 'Vendors',            path: '/vendors',                 sortOrder: 2, parentId: 'parties' },
      { key: 'projects',           label: 'Projects',           path: null,                       sortOrder: 4 },
      { key: 'manage-projects',    label: 'Manage Projects',    path: '/projects',                sortOrder: 1, parentId: 'projects' },
      { key: 'project-types',      label: 'Project Types',      path: '/project-types',           sortOrder: 2, parentId: 'projects' },
      { key: 'project-consumption',label: 'Project Consumption',path: '/projects/consumption',     sortOrder: 3, parentId: 'projects' },
      { key: 'inventory-management', label: 'Inventory Management', path: null,                  sortOrder: 5 },
      { key: 'manage-inventory',   label: 'Manage Inventory',   path: '/inventory',               sortOrder: 1, parentId: 'inventory-management' },
      { key: 'inventory-categories', label: 'Categories',       path: '/inventory/categories',    sortOrder: 2, parentId: 'inventory-management' },
      { key: 'warehouses',         label: 'Warehouses',         path: '/inventory/warehouses',    sortOrder: 3, parentId: 'inventory-management' },
      { key: 'transfers',          label: 'Stock Transfers',    path: '/inventory/transfers',     sortOrder: 4, parentId: 'inventory-management' },
      { key: 'finance',            label: 'Finance',            path: '/finance',                 sortOrder: 7 },
      { key: 'account-heads',      label: 'Account Heads',      path: '/finance/account-heads',   sortOrder: 8,  parentId: 'finance' },
      { key: 'transactions',       label: 'Transactions',       path: '/finance/transactions',    sortOrder: 9,  parentId: 'finance' },
      { key: 'customer-payments',  label: 'Receipts',           path: '/finance/customer-payments', sortOrder: 10, parentId: 'finance' },
      { key: 'vendor-purchases',   label: 'Purchases',          path: '/finance/vendor-purchases',  sortOrder: 11, parentId: 'finance' },
      { key: 'purchase-returns',   label: 'Purchase Returns',   path: '/finance/purchase-returns',  sortOrder: 12, parentId: 'finance' },
      { key: 'vendor-payments',    label: 'Payments',           path: '/finance/vendor-payments',   sortOrder: 13, parentId: 'finance' },
      { key: 'finance-reports',    label: 'Financial Reports',  path: '/finance/reports',           sortOrder: 14, parentId: 'finance' },
      { key: 'cash-bank',          label: 'Cash & Bank',        path: '/finance/cash-bank',         sortOrder: 14, parentId: 'finance' },
      { key: 'cash-advance',       label: 'Cash Advances',      path: '/finance/cash-advance',      sortOrder: 15, parentId: 'finance' },
      { key: 'payroll',            label: 'Payroll',            path: '/payroll',                   sortOrder: 16, parentId: 'finance' },
      { key: 'finance-approvals',  label: 'Approvals Queue',    path: '/finance/approvals',         sortOrder: 17, parentId: 'finance' },
      { key: 'finance-approval-settings', label: 'Approval Settings', path: '/finance/approval-settings', sortOrder: 18, parentId: 'finance' },
      { key: 'employee-management', label: 'Employee Management', path: null,                      sortOrder: 8 },
      { key: 'employees',          label: 'Employees',          path: '/employees',               sortOrder: 1, parentId: 'employee-management' },
      { key: 'users',              label: 'Users',              path: '/users',                   sortOrder: 2, parentId: 'employee-management' },
      { key: 'roles',              label: 'Roles',              path: '/roles',                   sortOrder: 3, parentId: 'employee-management' },
      { key: 'timesheets',                  label: 'Timesheets',          path: '/timesheets',                    sortOrder: 16 },
      { key: 'my-timesheets',               label: 'My Timesheets',       path: '/timesheets/my',                 sortOrder: 17, parentId: 'timesheets' },
      { key: 'timesheet-approvals',         label: 'Approvals',           path: '/timesheets/approvals',          sortOrder: 18, parentId: 'timesheets' },
      { key: 'supervisor-updates',          label: 'Supervisor Updates',  path: '/supervisor-updates/my',         sortOrder: 19 },
      { key: 'my-supervisor-updates',       label: 'My Updates',          path: '/supervisor-updates/my',         sortOrder: 20, parentId: 'supervisor-updates' },
      { key: 'supervisor-update-approvals', label: 'Approvals',           path: '/supervisor-updates/approvals',  sortOrder: 21, parentId: 'supervisor-updates' },
      { key: 'reports',            label: 'Reports',            path: '/reports',                 sortOrder: 17 },
      { key: 'settings',           label: 'Settings',           path: null,                       sortOrder: 24 },
      { key: 'company-settings',   label: 'Company Details',    path: '/settings/company',        sortOrder: 0, parentId: 'settings' },
      { key: 'email-settings',     label: 'Email Integration',  path: '/settings/email',          sortOrder: 1, parentId: 'settings' },
      { key: 'whatsapp-settings',  label: 'WhatsApp Integration', path: '/settings/whatsapp',     sortOrder: 2, parentId: 'settings' },
      { key: 'theme-settings',     label: 'Theme Customization', path: '/settings/theme',         sortOrder: 3, parentId: 'settings' },
      { key: 'branches',           label: 'Branches',           path: '/branches',                sortOrder: 4, parentId: 'settings' },
      { key: 'subscription',       label: 'Subscription',       path: '/subscription',            sortOrder: 5, parentId: 'settings' },
      { key: 'menus',              label: 'Menu Management',    path: '/menus',                   sortOrder: 6, parentId: 'settings' },
      { key: 'audit-logs',         label: 'Audit Logs',         path: '/audit-logs',              sortOrder: 7, parentId: 'settings' },
      { key: 'calendar',           label: 'Calendar',           path: '/calendar',                sortOrder: 25 },
    ];

    const financeKeys = ['finance', 'account-heads', 'transactions', 'customer-payments', 'vendor-purchases', 'purchase-returns', 'vendor-payments', 'finance-reports', 'cash-bank', 'cash-advance', 'payroll', 'finance-approvals', 'finance-approval-settings'];
    const inventoryKeys = ['inventory-management', 'manage-inventory', 'inventory-categories', 'warehouses', 'transfers'];
    const partiesKeys = ['parties', 'customers', 'vendors'];
    const projectsKeys = ['projects', 'manage-projects', 'project-types', 'project-consumption'];
    const employeeManagementKeys = ['employee-management', 'employees', 'users', 'roles'];
    const timesheetKeys = ['timesheets', 'my-timesheets'];
    const approvalKeys  = ['timesheets', 'my-timesheets', 'timesheet-approvals'];

    const suKeys = ['supervisor-updates', 'my-supervisor-updates'];
    const suAllKeys = ['supervisor-updates', 'my-supervisor-updates', 'supervisor-update-approvals'];

    switch (role) {
      case 'COMPANY_ADMIN':
        return allMenus.filter(m => [
          'dashboard', 'enquiries', ...partiesKeys, ...projectsKeys, ...inventoryKeys,
          ...financeKeys, ...employeeManagementKeys,
          ...approvalKeys, ...suAllKeys, 'calendar',
          'reports', 'menus', 'audit-logs', 'branches', 'subscription',
          'settings', 'company-settings', 'email-settings', 'whatsapp-settings', 'theme-settings'
        ].includes(m.key));
      case 'BRANCH_MANAGER':
        return allMenus.filter(m => [
          'dashboard', 'enquiries', ...partiesKeys, ...projectsKeys, ...inventoryKeys,
          ...financeKeys, ...employeeManagementKeys,
          ...approvalKeys, ...suAllKeys, 'calendar', 'reports'
        ].includes(m.key));
      case 'SUPERVISOR':
        return allMenus.filter(m => [
          'dashboard', 'enquiries', ...projectsKeys,
          ...approvalKeys, ...suKeys, 'calendar'
        ].includes(m.key));
      case 'EMPLOYEE':
        return allMenus.filter(m => ['dashboard', ...projectsKeys, ...timesheetKeys, ...suKeys, 'calendar'].includes(m.key));
      default:
        return [allMenus[0]];
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { accessToken, user: userData, planTier: tier, featureFlags: flags } = response.data;

      sessionStorage.setItem('token', accessToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.removeItem('userMenus'); // clear stale cache

      // Persist tenant claims
      if (tier) {
        sessionStorage.setItem('planTier', tier);
        setPlanTier(tier);
      }
      if (flags) {
        const flagsArray = Array.isArray(flags) ? flags : Object.keys(flags);
        sessionStorage.setItem('featureFlags', JSON.stringify(flagsArray));
        setFeatureFlags(flagsArray);
      }

      setUser(userData);
      if (userData?.companyCountry) {
        sessionStorage.setItem('companyCountry', userData.companyCountry);
        setCompanyCountry(userData.companyCountry);
      } else {
        sessionStorage.setItem('companyCountry', 'USA');
        setCompanyCountry('USA');
      }
      await fetchUserMenus(userData);

      // Store branch context — Requirements 9.3, 9.6
      if (response.data.branchId !== undefined) {
        sessionStorage.setItem('branchId', JSON.stringify(response.data.branchId));
        setBranchId(response.data.branchId);
      }
      if (response.data.branchName) {
        sessionStorage.setItem('branchName', response.data.branchName);
        setBranchName(response.data.branchName);
      }
      if (response.data.branchAssignments) {
        sessionStorage.setItem('branchAssignments', JSON.stringify(response.data.branchAssignments));
        setBranchAssignments(response.data.branchAssignments);
      }

      // Redirect super admin to their portal
      if (userData?.role === 'SUPER_ADMIN') {
        return { success: true, redirect: '/superadmin' };
      }

      // Force password change on first login
      if (userData?.passwordResetRequired) {
        return { success: true, redirect: '/change-password' };
      }

      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      // Backend returns ErrorResponse with { error: { code, message } } or { message }
      const msg = data?.error?.message || data?.message || data?.error;

      if (!error.response) {
        return { success: false, error: 'Cannot reach the server. Please check your connection.' };
      }
      if (status === 400 || status === 401) {
        return { success: false, error: 'Invalid email or password.' };
      }
      if (status === 403) {
        const code = data?.code || data?.error?.code;
        if (code === 'EMAIL_NOT_VERIFIED') {
          return { success: false, error: 'Please verify your email before logging in.' };
        }
        if (code === 'ACCOUNT_SUSPENDED') {
          return { success: false, error: 'Your account has been suspended. Contact support.' };
        }
        if (code === 'ACCOUNT_EXPIRED') {
          return { success: false, error: 'TRIAL_EXPIRED', redirect: '/trial-expired' };
        }
        if (code === 'ACCOUNT_PENDING_APPROVAL') {
          return { success: false, error: 'Your plan approval is pending. Please contact support.' };
        }
        return { success: false, error: msg || 'Access denied.' };
      }
      return { success: false, error: msg || 'Something went wrong. Please try again.' };
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('userMenus');
    sessionStorage.removeItem('planTier');
    sessionStorage.removeItem('featureFlags');
    sessionStorage.removeItem('branchId');
    sessionStorage.removeItem('branchName');
    sessionStorage.removeItem('branchAssignments');
    sessionStorage.removeItem('companyCountry');
    setUser(null);
    setUserMenus([]);
    setPlanTier(null);
    setFeatureFlags([]);
    setBranchId(null);
    setBranchName(null);
    setBranchAssignments([]);
    setCompanyCountry(null);
    navigate('/login');
  };

  const isAuthenticated = () => !!user && !!sessionStorage.getItem('token');

  const hasMenuAccess = (menuKey) => userMenus.some(menu => menu.key === menuKey);

  const hasFeature = (featureKey) => {
    if (!featureKey) return true;
    return featureFlags.includes(featureKey) || featureFlags.includes('ALL');
  };

  const isSuperAdmin = () => user?.role === 'SUPER_ADMIN';

  const isCompanyAdmin = () => user?.role === 'COMPANY_ADMIN';

  /**
   * Switch active branch. Calls PUT /api/v1/auth/switch-branch, stores new token,
   * updates branchName in state.
   * Requirements: 9.3, 9.4
   */
  const switchBranch = async (targetBranchId) => {
    try {
      const response = await axios.put('/auth/switch-branch', { branchId: targetBranchId });
      const { accessToken, branchName: newBranchName } = response.data;
      if (accessToken) {
        sessionStorage.setItem('token', accessToken);
      }
      if (newBranchName) {
        sessionStorage.setItem('branchName', newBranchName);
        setBranchName(newBranchName);
      }
      sessionStorage.setItem('branchId', JSON.stringify(targetBranchId));
      setBranchId(targetBranchId);
      // Trigger full page reload so all data components re-fetch for the new branch
      navigate(0);
      return { success: true };
    } catch (error) {
      console.error('Branch switch failed:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to switch branch' };
    }
  };

  const getBranchAssignments = () => branchAssignments;

  const value = {
    user,
    userMenus,
    planTier,
    featureFlags,
    branchId,
    branchName,
    branchAssignments,
    companyCountry,
    setCompanyCountry,
    login,
    logout,
    isAuthenticated,
    hasMenuAccess,
    hasFeature,
    isSuperAdmin,
    isCompanyAdmin,
    switchBranch,
    getBranchAssignments,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
